import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';

// Pages
import { Landing } from '@/pages/Landing';
import { Login } from '@/pages/Login';
import { Register } from '@/pages/Register';
import { Dashboard } from '@/pages/Dashboard';
import { Leagues } from '@/pages/Leagues';
import { LeagueDetail } from '@/pages/LeagueDetail';
import { Profile } from '@/pages/Profile';
import { AuthCallback } from '@/pages/AuthCallback';
import { GoogleAuthSuccess } from '@/pages/GoogleAuthSuccess';
import { AdminPanel } from '@/pages/AdminPanel';

// PWA Components
import { PWAInstallBanner } from '@/components/PWAInstallBanner';
import { PWAOfflineIndicator } from '@/components/PWAOfflineIndicator';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  console.log('🔒 [ProtectedRoute] Check per:', location.pathname);
  console.log('🔒 [ProtectedRoute] isLoading:', isLoading);
  console.log('🔒 [ProtectedRoute] isAuthenticated:', isAuthenticated);
  console.log('🔒 [ProtectedRoute] user:', user);

  if (isLoading) {
    console.log('⏳ [ProtectedRoute] Loading... mostro spinner');
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('❌ [ProtectedRoute] NON autenticato! Redirect a /login');
    return <Navigate to="/login" replace />;
  }

  console.log('✅ [ProtectedRoute] Autenticato! Accesso consentito a:', location.pathname);
  return children;
};

// Public Route - redirect to leagues if authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  console.log('🌍 [PublicRoute] Check per:', location.pathname);
  console.log('🌍 [PublicRoute] isLoading:', isLoading);
  console.log('🌍 [PublicRoute] isAuthenticated:', isAuthenticated);
  console.log('🌍 [PublicRoute] user:', user);

  if (isLoading) {
    console.log('⏳ [PublicRoute] Loading... mostro spinner');
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    console.log('✅ [PublicRoute] Autenticato! Redirect a /dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('ℹ️ [PublicRoute] Non autenticato, accesso consentito a:', location.pathname);
  return children;
};

const AppRoutes = () => {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - SYNCHRONOUS check to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      
      {/* Google Auth Success - Public route for cookie verification */}
      <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
      
      {/* Admin Panel - Public route (has its own auth) */}
      <Route path="/admin" element={<AdminPanel />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leagues"
        element={
          <ProtectedRoute>
            <Leagues />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leagues/:id"
        element={
          <ProtectedRoute>
            <LeagueDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <PWAOfflineIndicator />
          <AppRoutes />
          <Toaster position="top-center" richColors />
          <PWAInstallBanner />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
